import { gql } from 'graphql-tag';
import { TypedDocumentNode } from '@apollo/client';

import {
  GetConsents,
  GetConsentsVariables,
  GetCurrentUser,
  GetCurrentUserVariables,
  GetManageablePages,
  GetManageablePageStatuses,
  GetManageablePageStatusesVariables,
  GetManageablePagesVariables,
  GetPublishablePages,
  GetPublishablePagesVariables,
  GetUserProductUnits,
  GetUserProductUnitsVariables,
} from '@generated/graphql';
import { ImageFragments } from '@graphql/fragments/rutilus/ImageType';
import { PageFragments } from '@graphql/fragments/rutilus/PageType';
import { ProductFragments } from '@graphql/fragments/rutilus/ProductType';
import { ProductUnitFragments } from '@graphql/fragments/rutilus/ProductUnitType';
import { UserFragments } from '@graphql/fragments/rutilus/UserType';

export const getCurrentUser: TypedDocumentNode<GetCurrentUser, GetCurrentUserVariables> = gql`
  query GetCurrentUser {
    currentUser: me {
      ...CurrentUserFields
    }
  }
  ${UserFragments.currentUserFields}
`;

export const getManageablePageStatuses: TypedDocumentNode<
  GetManageablePageStatuses,
  GetManageablePageStatusesVariables
> = gql`
  query GetManageablePageStatuses {
    entry: me {
      ...CurrentUserId
      manageablePages(first: 50) {
        edges {
          node {
            ...ManageablePageStatusNodeFields
          }
        }
      }
    }
  }
  ${PageFragments.manageablePageStatusNodeFields}
  ${UserFragments.currentUserId}
`;

export const getManageablePages: TypedDocumentNode<
  GetManageablePages,
  GetManageablePagesVariables
> = gql`
  query GetManageablePages {
    entry: me {
      ...CurrentUserId
      manageablePages(first: 50) {
        edges {
          node {
            ...ManageablePageNodeFields
          }
        }
      }
    }
  }
  ${PageFragments.manageablePageNodeFields}
  ${UserFragments.currentUserId}
`;

export const getPublishablePages: TypedDocumentNode<
  GetPublishablePages,
  GetPublishablePagesVariables
> = gql`
  query GetPublishablePages {
    entry: me {
      ...CurrentUserId
      pagesCanPublishAs(first: 20) {
        edges {
          node {
            ...PageId
            name
            externalId
          }
        }
      }
    }
  }
  ${PageFragments.id}
  ${UserFragments.currentUserId}
`;

export const getUserProductUnits: TypedDocumentNode<
  GetUserProductUnits,
  GetUserProductUnitsVariables
> = gql`
  query GetUserProductUnits($categoryExternalIds: [String!]!) {
    entry: me {
      ...CurrentUserId
      productUnits(first: 100, categoryExternalIds: $categoryExternalIds) {
        edges {
          node {
            ...ProductUnitId
            externalId
            image(height: 96) {
              ...ImageFields
            }
            attributeValues(first: 3) {
              edges {
                node {
                  value {
                    name
                  }
                }
              }
            }
            product {
              __typename
              ...ProductId
              ...ProductBrandName
              displayName
            }
          }
        }
      }
    }
  }
  ${ImageFragments.imageFields}
  ${ProductFragments.brandName}
  ${ProductFragments.id}
  ${ProductUnitFragments.id}
  ${UserFragments.currentUserId}
`;

export const getConsents: TypedDocumentNode<GetConsents, GetConsentsVariables> = gql`
  query GetConsents {
    me {
      ...CurrentUserId
      consents(last: 10) {
        edges {
          node {
            id
            accepted
            agreementUid
          }
        }
      }
    }
  }
  ${UserFragments.currentUserId}
`;
